import * as React from 'react';
import { View } from '../../../types';
import { useSelector } from '../../../utils/useSelector';
import { useDrop } from 'react-dnd';
import { EFSItemCard } from './EFSItemCard';
import { useActions as useEFSActions } from './EFSEditMode/EFSEditModeActions';
import { Overlay } from 'rcd-cpp-ui/ui/loader/Overlay';
import { DragItem } from '../../../types/DragItem';

export interface EFSGroupItemsProps {
  group: View.Group;
}

export const EFSGroupItems = ({ group }: EFSGroupItemsProps) => {
  const allCards = useSelector(s => s.view.cards);
  const efsItemData = useSelector(s => s.view.efsItemData);
  const selectedEFSPersona = useSelector(s => s.view.selectedEFSPersona);
  const editMode = useSelector(s => s.view.editMode);
  const cardRefIds = group.cardRefIds;
  const sortedItems = React.useMemo(() => {
    if (!cardRefIds?.length || !efsItemData || !selectedEFSPersona) return [];
    return sortEFSItems(cardRefIds, allCards, selectedEFSPersona, efsItemData);
  }, [cardRefIds, allCards, selectedEFSPersona, efsItemData]);

  const { onDropEFSItem } = useEFSActions();

  const moveItem = (
    dragIndex: number,
    hoverIndex: number,
    timeFrame: number | null
  ) => {
    const itemsInTimeFrame = sortedItems.filter(refId => {
      const itemId = allCards[refId]?.props?.itemId;
      const matchingPersona = efsItemData[itemId]?.efsPersonas.find(
        efs =>
          efs.efsCompanyDirectionId ==
            selectedEFSPersona?.efsCompanyDirectionId &&
          efs.efsExperienceId == selectedEFSPersona?.efsExperienceId
      );
      const itemTimeFrame = matchingPersona?.efsTimeFrame ?? null;
      return itemTimeFrame == timeFrame;
    });

    const updatedItems = [...itemsInTimeFrame];
    const [draggedItem] = updatedItems.splice(dragIndex, 1);
    updatedItems.splice(hoverIndex, 0, draggedItem);
    const updatedDisplayOrders = updatedItems.map((item, index) => {
      const itemId = allCards[item]?.props?.itemId;
      const matchingEfs = efsItemData[itemId]?.efsPersonas.find(
        efs =>
          efs.efsCompanyDirectionId ===
            selectedEFSPersona?.efsCompanyDirectionId &&
          efs.efsExperienceId === selectedEFSPersona?.efsExperienceId
      );
      if (matchingEfs) {
        matchingEfs.efsDisplayOrder = index + 1;
      }
      return { refId: item, efsDisplayOrder: index + 1 };
    });

    onDropEFSItem(updatedDisplayOrders);
  };

  const timeFrameGroups = React.useMemo(() => {
    const groups: Record<number | string, string[]> = {};
    sortedItems.forEach(refId => {
      const itemId = allCards[refId]?.props?.itemId;
      const matchingPersona = efsItemData[itemId]?.efsPersonas.find(
        efs =>
          efs.efsCompanyDirectionId ===
            selectedEFSPersona?.efsCompanyDirectionId &&
          efs.efsExperienceId === selectedEFSPersona?.efsExperienceId
      );
      const timeFrame = matchingPersona?.efsTimeFrame ?? 'null';
      if (!groups[timeFrame]) {
        groups[timeFrame] = [];
      }
      groups[timeFrame].push(refId);
    });
    return groups;
  }, [sortedItems, allCards, selectedEFSPersona, efsItemData]);

  return (
    <div className="board-group-items">
      {cardRefIds?.length > 0 && !efsItemData && <Overlay spinnerSize="icon" />}
      {Object.entries(timeFrameGroups).map(([timeFrame, items]) => {
        return (
          <EFSItemCardWrapper
            editMode={editMode}
            group={group}
            timeFrame={timeFrame}
            items={items}
            moveItem={moveItem}
          />
        );
      })}
    </div>
  );
};

const EFSItemCardWrapper = ({
  editMode,
  group,
  timeFrame,
  items,
  moveItem
}) => {
  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: 'EFS_ITEM',
    canDrop: (draggedItem: DragItem) =>
      editMode &&
      draggedItem.columnId === group.id &&
      (draggedItem.efsTimeFrame === Number(timeFrame) ||
        (timeFrame === 'null' && draggedItem.efsTimeFrame === null)),
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  return (
    <div
      key={timeFrame}
      ref={editMode ? dropRef : null}
      className={`time-frame-container ${
        canDrop ? 'droppable-container' : ''
      } ${isOver && !canDrop ? 'non-droppable-container' : ''}`}
    >
      {items.map((refId, index) => (
        <EFSItemCard
          key={refId}
          group={group}
          refId={refId}
          index={index}
          moveItem={(dragIndex, hoverIndex) =>
            moveItem(
              dragIndex,
              hoverIndex,
              timeFrame === 'null' ? null : Number(timeFrame)
            )
          }
          efsTimeFrame={timeFrame === 'null' ? null : Number(timeFrame)}
          columnId={group.id}
          editMode={editMode}
        />
      ))}
    </div>
  );
};

export const sortEFSItems = (
  efsCardRefIds: string[],
  allCards: Record<string, any>,
  selectedEFSPersona: {
    efsCompanyDirectionId: number;
    efsExperienceId: number;
  } | null,
  efsItemData: Record<string, any>
): string[] => {
  const sortedItems = [...efsCardRefIds].sort((a, b) => {
    const itemIdA = allCards[a]?.props?.itemId;
    const itemIdB = allCards[b]?.props?.itemId;

    const matchingPersonaA = efsItemData[itemIdA]?.efsPersonas.find(
      efs =>
        efs.efsCompanyDirectionId ===
          selectedEFSPersona?.efsCompanyDirectionId &&
        efs.efsExperienceId === selectedEFSPersona?.efsExperienceId
    );
    const matchingPersonaB = efsItemData[itemIdB]?.efsPersonas.find(
      efs =>
        efs.efsCompanyDirectionId ===
          selectedEFSPersona?.efsCompanyDirectionId &&
        efs.efsExperienceId === selectedEFSPersona?.efsExperienceId
    );

    const timeFrameA = matchingPersonaA?.efsTimeFrame ?? Infinity;
    const timeFrameB = matchingPersonaB?.efsTimeFrame ?? Infinity;
    if (timeFrameA !== timeFrameB) {
      return timeFrameA - timeFrameB;
    }

    const displayOrderA = matchingPersonaA?.efsDisplayOrder ?? -Infinity;
    const displayOrderB = matchingPersonaB?.efsDisplayOrder ?? -Infinity;
    if (displayOrderA !== displayOrderB) {
      return displayOrderA - displayOrderB;
    }

    return (itemIdA ?? -Infinity) - (itemIdB ?? -Infinity);
  });
  return sortedItems;
};
